import { useQuery, useMutation } from "@tanstack/react-query";
import { EditorApi } from "../api/editor-api";
import { VoiceOverApi } from "../api/voice-over-api";
import type {
  CreateScriptChatResponseDto,
  ScriptChatMessageDto,
} from "@repo/api/voice-over";
import { useUser } from "@clerk/react";

export function usePreviouslyUsedVoiceActorsQuery(teamExternalId: string) {
  return useQuery({
    queryKey: voiceOverQueryKeys.getPreviouslyUsedVoiceActors(teamExternalId),
    queryFn: () => EditorApi.getPreviouslyUsedVoiceActors(teamExternalId),
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch previously used voice actors",
    },
  });
}

export function useActiveScriptChatQuery(
  teamExternalId: string,
  videoExternalId: string,
  enabled: boolean = true,
) {
  const { user } = useUser();

  return useQuery({
    queryKey: voiceOverQueryKeys.getActiveScriptChat(
      user?.id,
      teamExternalId,
      videoExternalId,
    ),
    queryFn: () =>
      VoiceOverApi.getActiveScriptChat(teamExternalId, videoExternalId),
    enabled: enabled && !!user?.id,
    retry: false,
  });
}

export function useCreateScriptChatMutation({
  teamExternalId,
  videoExternalId,
  onSuccess,
  onError,
  onMutate,
}: {
  teamExternalId: string;
  videoExternalId: string;
  onSuccess?: (data: CreateScriptChatResponseDto) => void;
  onError?: (error: Error, variables: void, context: unknown) => void;
  onMutate?: () => Promise<unknown> | unknown;
}) {
  return useMutation({
    mutationKey: voiceOverQueryKeys.createScriptChat(
      teamExternalId,
      videoExternalId,
    ),
    mutationFn: () =>
      VoiceOverApi.createScriptChat(teamExternalId, videoExternalId),
    onSuccess,
    onMutate,
    onError,
  });
}

export async function sendScriptChatMessage(
  teamExternalId: string,
  chatExternalId: string,
  message: string,
  callbacks: {
    onUserMessage?: (message: ScriptChatMessageDto) => void;
    onChunk?: (chunk: string) => void;
    onDone?: (message: ScriptChatMessageDto) => void;
    onError?: (error: string) => void;
  },
  signal?: AbortSignal,
): Promise<void> {
  const response = await VoiceOverApi.sendMessage(
    teamExternalId,
    chatExternalId,
    message,
    signal,
  );

  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try {
          const data = JSON.parse(line.slice(6));

          if ("error" in data) {
            callbacks.onError?.(data.error);
          } else if (data.type === "user_message") {
            callbacks.onUserMessage?.(data.message);
          } else if (data.type === "chunk") {
            callbacks.onChunk?.(data.chunk);
          } else if (data.type === "done") {
            callbacks.onDone?.(data.message);
          }
        } catch (e) {
          console.error("Failed to parse SSE event:", e);
        }
      }
    }
  }
}

export const voiceOverQueryKeys = {
  getPreviouslyUsedVoiceActors: (teamExternalId: string) => [
    "voiceOver",
    "previouslyUsedVoiceActors",
    { teamExternalId },
  ],
  getActiveScriptChat: (
    userId: string | undefined,
    teamExternalId: string,
    videoExternalId: string,
  ) => ["voiceOver", "scriptChat", { userId, teamExternalId, videoExternalId }],
  createScriptChat: (teamExternalId: string, videoExternalId: string) => [
    "voiceOver",
    "createScriptChat",
    { teamExternalId, videoExternalId },
  ],
};
